'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import checkSessionValidity from '~/clientUtils/checkSessionValidity';

interface UseSessionMonitorOptions {
    checkInterval?: number;
}

export const useSessionMonitor = (options: UseSessionMonitorOptions = {}) => {
    const {
        checkInterval = 15000, // 15 seconds
    } = options;

    const redirectPath = '/';

    const [isInitialCheckDone, setIsInitialCheckDone] = useState<boolean>(false);
    const [hasValidSession, setHasValidSession] = useState<boolean>(false);
    const intervalRef = useRef<NodeJS.Timeout>();
    const isCheckingRef = useRef(false);

    const handleSessionInvalid = useCallback(() => {
        // Clear the interval to stop checking
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }

        // Redirect to specified path with full page reload
        window.location.replace(redirectPath);
    }, [redirectPath]);

    const checkSession = useCallback(async () => {
        // Prevent multiple simultaneous checks
        if (isCheckingRef.current) {
            return;
        }

        isCheckingRef.current = true;

        try {
            const sessionValid = await checkSessionValidity();

            if (!sessionValid?.valid) {
                handleSessionInvalid();
            }
        } catch (error) {
            // If check fails, consider session invalid
            handleSessionInvalid();
        } finally {
            isCheckingRef.current = false;
        }
    }, [handleSessionInvalid]);

    const startMonitoring = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(checkSession, checkInterval);
    }, [checkSession, checkInterval]);

    const stopMonitoring = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }
    }, []);

    const initialCheck = async () => {
        try {
            const sessionCheck = await checkSessionValidity();
            setHasValidSession(sessionCheck.valid);
        } catch (error) {
            setHasValidSession(false);
        } finally {
            setIsInitialCheckDone(true);
        }
    };

    useEffect(() => {
        if (!isInitialCheckDone) {
            initialCheck();
        } else {
            if (hasValidSession) {
                startMonitoring();
            } else {
                stopMonitoring();
            }
        }

        return () => {
            stopMonitoring();
        };
    }, [isInitialCheckDone, hasValidSession, startMonitoring, stopMonitoring]);

    return {
        startMonitoring,
        stopMonitoring,
        checkSession,
    };
};
